import { Component, Input } from '@angular/core';

/**
 * Responsive filter toolbar that arranges filter inputs on the left and
 * action buttons on the right.
 *
 * Project content using two attribute slots:
 *   - `filters`: filter inputs (search, dropdowns, "More Filters" button, etc.)
 *   - `actions`: action buttons (export, add, etc.)
 *
 * Wrap progression as the viewport narrows:
 *   1. All on one row.
 *   2. Actions group wraps to its own row, left-aligned below filters.
 *   3. Filters wrap internally (e.g. "More Filters" drops to its own row).
 *   4. Each filter stacks vertically.
 *
 * @example
 * <app-filter-toolbar [filtersMinWidth]="'660px'">
 *     <ng-container filters>
 *         <app-user-access-search ...></app-user-access-search>
 *         <app-select ...></app-select>
 *     </ng-container>
 *     <ng-container actions>
 *         <app-button label="Export" />
 *     </ng-container>
 * </app-filter-toolbar>
 */
@Component({
    selector: 'app-filter-toolbar',
    templateUrl: './filter-toolbar.component.html',
    styleUrls: ['./filter-toolbar.component.scss'],
    standalone: true,
})
export class FilterToolbarComponent {
    /**
     * Unwrapped width of the filters slot — used as a floor so the actions
     * group wraps to its own row before any of the filters wrap internally.
     * Must be a valid CSS length (e.g. `'660px'`); invalid values silently
     * fall back to `auto`. The value is applied as
     * `min-width: min(<value>, 100%)`, so it never causes the slot to exceed
     * the parent's width at the narrowest viewports.
     *
     * The default (`'660px'`) matches a search input (320px) + a small
     * dropdown (180px) + a "More Filters" button (144px) + two 8px gaps.
     * Set a larger value when projecting wider filter content.
     */
    @Input() filtersMinWidth: string = '660px';

    /** Computed `min-width` style for the filters slot. */
    get filtersMinWidthStyle(): string {
        return `min(${this.filtersMinWidth}, 100%)`;
    }
}
